import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, CheckCircle, Book, FileText, Award, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface LessonContent {
  id: number;
  title: string;
  type: string;
  duration: string;
  slides?: string[];
  quiz?: Question[];
}

interface LessonViewerProps {
  lesson: LessonContent;
  onComplete: (score?: number) => void;
  onClose: () => void;
}

const sampleLessonContent: Record<number, LessonContent> = {
  1: {
    id: 1,
    title: "What is Artificial Intelligence?",
    type: "reading",
    duration: "45 min",
    slides: [
      "# What is Artificial Intelligence?\n\nArtificial Intelligence (AI) is the simulation of human intelligence in machines that are programmed to think and learn like humans.\n\n## Key Characteristics:\n• **Learning** - Acquiring information and rules\n• **Reasoning** - Using rules to reach conclusions\n• **Problem-solving** - Finding solutions to complex challenges\n• **Perception** - Interpreting sensory data",
      
      "# Types of AI\n\n## Narrow AI (Weak AI)\n• Designed for specific tasks\n• Examples: Voice assistants, recommendation systems\n• Current state of most AI applications\n\n## General AI (Strong AI)\n• Human-level intelligence across all domains\n• Theoretical concept\n• Future goal of AI research",
      
      "# Real-World Applications\n\n## Healthcare\n• Medical diagnosis and imaging\n• Drug discovery and development\n• Personalized treatment plans\n\n## Transportation\n• Autonomous vehicles\n• Traffic optimization\n• Route planning\n\n## Finance\n• Fraud detection\n• Algorithmic trading\n• Credit scoring"
    ]
  },
  2: {
    id: 2,
    title: "History and Evolution of AI",
    type: "reading",
    duration: "30 min",
    slides: [
      "# History and Evolution of AI\n\n## The Birth of AI (1940s-1950s)\n• **1943**: McCulloch and Pitts create first artificial neural network\n• **1950**: Alan Turing publishes \"Computing Machinery and Intelligence\"\n• **1956**: Dartmouth Conference - AI term coined\n\n## The Turing Test\n• Proposed by Alan Turing in 1950\n• Tests machine's ability to exhibit intelligent behavior\n• Still relevant benchmark today",
      
      "# AI Winters and Springs\n\n## First AI Winter (1970s)\n• Limited computational power\n• Overpromised capabilities\n• Reduced funding and interest\n\n## AI Renaissance (1980s)\n• Expert systems emerge\n• Knowledge-based approaches\n• Commercial applications grow\n\n## Second AI Winter (Late 1980s-1990s)\n• Expert systems limitations\n• Market disappointment\n• Shift to statistical methods",
      
      "# Modern AI Era (2000s-Present)\n\n## Key Breakthroughs\n• **2006**: Deep learning revolution begins\n• **2012**: ImageNet breakthrough with AlexNet\n• **2016**: AlphaGo defeats world champion\n• **2020**: GPT-3 demonstrates language capabilities\n• **2022**: ChatGPT achieves mainstream adoption\n\n## Current Trends\n• Large Language Models (LLMs)\n• Generative AI applications\n• AI democratization and accessibility"
    ]
  },
  3: {
    id: 3,
    title: "AI vs Machine Learning vs Deep Learning",
    type: "reading",
    duration: "50 min",
    slides: [
      "# Understanding the Relationship\n\n## The Hierarchy\n• **Artificial Intelligence** - The umbrella term\n• **Machine Learning** - Subset of AI\n• **Deep Learning** - Subset of ML\n\n## Think of it as nested circles:\n• AI is the largest circle\n• ML is inside AI\n• Deep Learning is inside ML\n\nEach builds upon the previous level",
      
      "# Artificial Intelligence (AI)\n\n## Definition\n• Broad field of computer science\n• Creating machines that can perform tasks requiring human intelligence\n• Includes reasoning, learning, perception, language understanding\n\n## Types of AI Implementation\n• **Rule-based systems** - Programmed logic\n• **Machine learning** - Learning from data\n• **Hybrid approaches** - Combining multiple methods\n\n## Examples\n• Chess programs, expert systems, chatbots, robotics",
      
      "# Machine Learning (ML)\n\n## Core Concept\n• Algorithms that improve through experience\n• Learning patterns from data without explicit programming\n• Statistical approach to AI\n\n## Types of Machine Learning\n• **Supervised Learning** - Learning with labeled examples\n• **Unsupervised Learning** - Finding patterns in unlabeled data\n• **Reinforcement Learning** - Learning through trial and error\n\n## Common Algorithms\n• Linear regression, decision trees, support vector machines, random forests",
      
      "# Deep Learning (DL)\n\n## What Makes it \"Deep\"\n• Neural networks with multiple hidden layers\n• Inspired by human brain structure\n• Automatically learns feature representations\n\n## Key Advantages\n• Excellent for complex pattern recognition\n• Works well with large datasets\n• Minimal feature engineering required\n\n## Applications\n• Image recognition, natural language processing, speech recognition, game playing\n\n## Popular Architectures\n• Convolutional Neural Networks (CNNs)\n• Recurrent Neural Networks (RNNs)\n• Transformers"
    ]
  },
  4: {
    id: 4,
    title: "Quiz: AI Fundamentals",
    type: "quiz",
    duration: "15 min",
    quiz: [
      {
        id: 1,
        question: "Which of the following best describes machine learning?",
        options: [
          "A subset of AI that enables machines to learn from data",
          "A programming language for AI",
          "A type of computer hardware",
          "A method for storing large datasets"
        ],
        correctAnswer: 0,
        explanation: "Machine learning is indeed a subset of AI that focuses on algorithms that can learn and improve from data without being explicitly programmed."
      },
      {
        id: 2,
        question: "What is the difference between supervised and unsupervised learning?",
        options: [
          "Supervised uses labeled data, unsupervised uses unlabeled data",
          "Supervised is faster than unsupervised",
          "Supervised uses more memory than unsupervised",
          "There is no difference"
        ],
        correctAnswer: 0,
        explanation: "The key difference is that supervised learning uses labeled training data, while unsupervised learning finds patterns in unlabeled data."
      },
      {
        id: 3,
        question: "Which of these is an example of AI bias?",
        options: [
          "A facial recognition system that works better on certain demographics",
          "An AI system that runs slowly",
          "A chatbot that speaks multiple languages",
          "A recommendation system that suggests popular items"
        ],
        correctAnswer: 0,
        explanation: "AI bias occurs when systems perform differently across demographic groups, often due to biased training data or algorithms."
      },
      {
        id: 4,
        question: "What was the significance of the 1956 Dartmouth Conference?",
        options: [
          "It introduced the first computer",
          "It coined the term 'Artificial Intelligence'",
          "It created the internet",
          "It developed the first programming language"
        ],
        correctAnswer: 1,
        explanation: "The 1956 Dartmouth Conference is widely considered the birth of AI as a field, where the term 'Artificial Intelligence' was first coined."
      }
    ]
  },
  5: {
    id: 5,
    title: "Types of Machine Learning",
    type: "reading",
    duration: "40 min",
    slides: [
      "# Types of Machine Learning\n\n## Three Main Categories\n• **Supervised Learning** - Learning with labeled examples\n• **Unsupervised Learning** - Finding patterns without labels\n• **Reinforcement Learning** - Learning through rewards and penalties\n\nEach type solves different kinds of problems and uses different approaches.",
      
      "# Supervised Learning\n\n## How it Works\n• Algorithm learns from input-output pairs\n• Training data includes correct answers\n• Model makes predictions on new data\n\n## Two Main Types\n• **Classification** - Predicts categories (spam/not spam)\n• **Regression** - Predicts continuous values (house prices)\n\n## Common Algorithms\n• Linear regression, logistic regression\n• Decision trees, random forests\n• Support vector machines, neural networks",
      
      "# Unsupervised Learning\n\n## Key Characteristics\n• No labeled training data\n• Algorithm finds hidden patterns\n• Exploratory data analysis\n\n## Main Applications\n• **Clustering** - Grouping similar data points\n• **Dimensionality Reduction** - Simplifying data\n• **Association Rules** - Finding relationships\n\n## Real-World Examples\n• Customer segmentation\n• Anomaly detection\n• Data compression",
      
      "# Reinforcement Learning\n\n## Core Concept\n• Agent learns through interaction with environment\n• Receives rewards or penalties for actions\n• Goal: maximize cumulative reward\n\n## Key Components\n• **Agent** - The learner\n• **Environment** - The world agent interacts with\n• **Actions** - What agent can do\n• **Rewards** - Feedback from environment\n\n## Famous Applications\n• Game playing (AlphaGo, chess)\n• Robotics and autonomous vehicles\n• Recommendation systems"
    ]
  },
  6: {
    id: 6,
    title: "Supervised Learning Deep Dive",
    type: "reading",
    duration: "55 min",
    slides: [
      "# Supervised Learning Deep Dive\n\n## The Foundation of Modern AI\n• Most common type of machine learning\n• Powers many real-world applications\n• Requires labeled training data\n• Learns mapping from inputs to outputs\n\n## Success Stories\n• Image recognition, email filtering, medical diagnosis, fraud detection",
      
      "# Classification Problems\n\n## Binary Classification\n• Two possible outcomes (Yes/No, True/False)\n• Examples: Email spam detection, medical diagnosis\n\n## Multi-class Classification\n• Multiple categories (A, B, C, D)\n• Examples: Image recognition, sentiment analysis\n\n## Multi-label Classification\n• Multiple labels can be true simultaneously\n• Examples: Movie genres, text tagging",
      
      "# Regression Problems\n\n## Predicting Continuous Values\n• Output is a number on a continuous scale\n• Linear and non-linear relationships\n\n## Common Applications\n• **House price prediction** - Based on size, location, features\n• **Stock price forecasting** - Using historical data\n• **Sales forecasting** - Predicting future revenue\n• **Weather prediction** - Temperature, rainfall amounts",
      
      "# Popular Algorithms\n\n## Linear Models\n• **Linear Regression** - Best fit line through data\n• **Logistic Regression** - For classification problems\n• Simple to understand and implement\n\n## Tree-Based Methods\n• **Decision Trees** - Series of if-then rules\n• **Random Forest** - Multiple trees voting\n• **Gradient Boosting** - Sequential tree improvement\n\n## Instance-Based Learning\n• **K-Nearest Neighbors** - Classification by similarity\n• **Support Vector Machines** - Finding optimal boundaries"
    ]
  },
  7: {
    id: 7,
    title: "Unsupervised Learning Applications",
    type: "reading",
    duration: "45 min",
    slides: [
      "# Unsupervised Learning Applications\n\n## Discovering Hidden Patterns\n• No predetermined answers\n• Exploratory data analysis\n• Finding structure in data\n• Often used for data preprocessing\n\n## Why It Matters\n• Most real-world data is unlabeled\n• Helps understand data before supervised learning\n• Reveals insights humans might miss",
      
      "# Clustering Applications\n\n## Customer Segmentation\n• Group customers by behavior patterns\n• Targeted marketing campaigns\n• Personalized product recommendations\n\n## Market Research\n• Identify customer segments\n• Product positioning strategies\n• Competitive analysis\n\n## Image Segmentation\n• Medical imaging analysis\n• Object detection preprocessing\n• Computer vision applications",
      
      "# Dimensionality Reduction\n\n## The Curse of Dimensionality\n• Too many features can hurt performance\n• Visualization becomes impossible\n• Computational complexity increases\n\n## Principal Component Analysis (PCA)\n• Finds most important directions in data\n• Reduces dimensions while preserving information\n• Widely used preprocessing technique\n\n## Applications\n• Data visualization\n• Noise reduction\n• Feature selection\n• Compression",
      
      "# Anomaly Detection\n\n## Identifying Outliers\n• Data points that don't fit normal patterns\n• Critical for security and quality control\n\n## Real-World Applications\n• **Fraud Detection** - Unusual transaction patterns\n• **Network Security** - Detecting cyber attacks\n• **Quality Control** - Finding defective products\n• **Healthcare** - Identifying rare diseases\n• **Predictive Maintenance** - Equipment failure prediction"
    ]
  },
  8: {
    id: 8,
    title: "Quiz: ML Fundamentals",
    type: "quiz",
    duration: "20 min",
    quiz: [
      {
        id: 1,
        question: "Which type of machine learning would you use to group customers based on their purchasing behavior without knowing the groups in advance?",
        options: [
          "Supervised learning",
          "Unsupervised learning",
          "Reinforcement learning",
          "Semi-supervised learning"
        ],
        correctAnswer: 1,
        explanation: "Unsupervised learning, specifically clustering, is used to group data points without predefined categories or labels."
      },
      {
        id: 2,
        question: "What is the main difference between classification and regression?",
        options: [
          "Classification predicts categories, regression predicts continuous values",
          "Classification is faster than regression",
          "Regression uses more data than classification",
          "There is no significant difference"
        ],
        correctAnswer: 0,
        explanation: "Classification predicts discrete categories or classes, while regression predicts continuous numerical values."
      },
      {
        id: 3,
        question: "In reinforcement learning, what guides the agent's learning process?",
        options: [
          "Labeled training examples",
          "Rewards and penalties from the environment",
          "Predefined rules",
          "Random exploration only"
        ],
        correctAnswer: 1,
        explanation: "Reinforcement learning agents learn through rewards and penalties received from their environment based on their actions."
      }
    ]
  },
  9: {
    id: 9,
    title: "Introduction to Neural Networks",
    type: "reading",
    duration: "50 min",
    slides: [
      "# Introduction to Neural Networks\n\n## Inspired by the Brain\n• Neural networks mimic biological neurons\n• Artificial neurons process and transmit information\n• Networks learn through pattern recognition\n• Foundation of modern deep learning\n\n## Basic Structure\n• **Input layer** - Receives data\n• **Hidden layers** - Process information\n• **Output layer** - Produces results",
      
      "# The Perceptron\n\n## Simplest Neural Network\n• Single artificial neuron\n• Takes weighted inputs\n• Applies activation function\n• Produces binary output\n\n## Mathematical Model\n• **Inputs**: x₁, x₂, ..., xₙ\n• **Weights**: w₁, w₂, ..., wₙ\n• **Bias**: b\n• **Output**: f(Σ(wᵢ × xᵢ) + b)\n\n## Limitations\n• Can only solve linearly separable problems\n• Cannot learn XOR function\n• Led to \"AI Winter\" in 1970s",
      
      "# Multilayer Neural Networks\n\n## Solving Complex Problems\n• Multiple hidden layers\n• Non-linear activation functions\n• Can approximate any function\n• Universal approximation theorem\n\n## Backpropagation Algorithm\n• **Forward pass** - Calculate predictions\n• **Loss calculation** - Measure errors\n• **Backward pass** - Update weights\n• **Gradient descent** - Minimize loss\n\n## Key Breakthrough\n• Made training deep networks possible\n• Revolutionized machine learning in 1980s"
    ]
  },
  10: {
    id: 10,
    title: "Deep Learning Architectures",
    type: "reading",
    duration: "60 min",
    slides: [
      "# Deep Learning Architectures\n\n## What Makes it Deep?\n• Multiple hidden layers (3+ typically)\n• Hierarchical feature learning\n• Automatic feature extraction\n• End-to-end learning capability\n\n## Key Advantages\n• Minimal manual feature engineering\n• Excellent performance on complex tasks\n• Scales well with data and compute\n• State-of-the-art in many domains",
      
      "# Convolutional Neural Networks (CNNs)\n\n## Designed for Visual Data\n• **Convolution layers** - Detect features\n• **Pooling layers** - Reduce dimensions\n• **Fully connected layers** - Classification\n\n## Key Concepts\n• **Filters/Kernels** - Feature detectors\n• **Feature maps** - Detected patterns\n• **Translation invariance** - Position independence\n• **Parameter sharing** - Efficient learning\n\n## Applications\n• Image classification, object detection\n• Medical imaging, autonomous vehicles\n• Computer vision tasks",
      
      "# Recurrent Neural Networks (RNNs)\n\n## Processing Sequential Data\n• Memory mechanism for sequences\n• Hidden state carries information\n• Can handle variable-length inputs\n• Suitable for time-series data\n\n## Variants\n• **Vanilla RNN** - Basic sequential processing\n• **LSTM** - Long Short-Term Memory\n• **GRU** - Gated Recurrent Unit\n\n## Applications\n• Natural language processing\n• Speech recognition, machine translation\n• Time-series forecasting",
      
      "# Transformer Architecture\n\n## Revolution in AI\n• **Attention mechanism** - Focus on relevant parts\n• **Self-attention** - Relate different positions\n• **Parallel processing** - Faster training\n• **Positional encoding** - Sequence understanding\n\n## Key Innovation\n• \"Attention is All You Need\" (2017)\n• Replaced RNNs for many NLP tasks\n• Foundation of GPT, BERT, ChatGPT\n\n## Impact\n• Enabled large language models\n• Breakthrough in language understanding\n• Scalable to massive datasets"
    ]
  },
  11: {
    id: 11,
    title: "Training Deep Neural Networks",
    type: "reading",
    duration: "40 min",
    slides: [
      "# Training Deep Neural Networks\n\n## The Learning Process\n• **Initialization** - Set starting weights\n• **Forward propagation** - Make predictions\n• **Loss computation** - Measure errors\n• **Backpropagation** - Calculate gradients\n• **Weight updates** - Improve performance\n\n## Challenges\n• Vanishing/exploding gradients\n• Overfitting to training data\n• Computational complexity\n• Local minima in loss landscape",
      
      "# Optimization Techniques\n\n## Gradient Descent Variants\n• **SGD** - Stochastic Gradient Descent\n• **Adam** - Adaptive Moment Estimation\n• **RMSprop** - Root Mean Square Propagation\n• **AdaGrad** - Adaptive Gradient\n\n## Learning Rate Strategies\n• **Fixed learning rate** - Constant step size\n• **Learning rate decay** - Gradually reduce\n• **Learning rate scheduling** - Systematic changes\n• **Adaptive learning rates** - Per-parameter adjustment",
      
      "# Regularization Methods\n\n## Preventing Overfitting\n• **Dropout** - Randomly disable neurons\n• **L1/L2 regularization** - Penalty terms\n• **Batch normalization** - Normalize inputs\n• **Early stopping** - Stop before overfitting\n\n## Data Augmentation\n• **Image transformations** - Rotation, scaling\n• **Noise injection** - Add random noise\n• **Synthetic data** - Generate new examples\n• **Cross-validation** - Robust evaluation\n\n## Best Practices\n• Monitor training and validation loss\n• Use appropriate batch sizes\n• Initialize weights properly\n• Regular checkpointing"
    ]
  },
  12: {
    id: 12,
    title: "Quiz: Deep Learning",
    type: "quiz",
    duration: "25 min",
    quiz: [
      {
        id: 1,
        question: "What makes deep learning 'deep'?",
        options: [
          "It processes data very slowly",
          "It uses neural networks with multiple hidden layers",
          "It requires more storage space",
          "It only works with text data"
        ],
        correctAnswer: 1,
        explanation: "Deep learning is called 'deep' because it uses neural networks with multiple hidden layers, allowing for complex pattern recognition and hierarchical feature learning."
      },
      {
        id: 2,
        question: "Which architecture is specifically designed for processing sequential data?",
        options: [
          "Convolutional Neural Networks (CNNs)",
          "Recurrent Neural Networks (RNNs)",
          "Feedforward Neural Networks",
          "Autoencoders"
        ],
        correctAnswer: 1,
        explanation: "RNNs are specifically designed for sequential data, with memory mechanisms that can process variable-length sequences and maintain context over time."
      },
      {
        id: 3,
        question: "What is the main innovation of the Transformer architecture?",
        options: [
          "Using only convolutional layers",
          "The attention mechanism",
          "Eliminating all neural networks",
          "Using only linear transformations"
        ],
        correctAnswer: 1,
        explanation: "The main innovation of Transformers is the attention mechanism, particularly self-attention, which allows the model to focus on relevant parts of the input sequence."
      }
    ]
  },
  13: {
    id: 13,
    title: "Foundations of Prompt Engineering",
    type: "reading",
    duration: "45 min",
    slides: [
      "# Foundations of Prompt Engineering\n\n## What is Prompt Engineering?\n• Art and science of crafting effective prompts\n• Communication interface with AI models\n• Bridge between human intent and AI understanding\n• Critical skill for AI applications\n\n## Why It Matters\n• Determines AI output quality\n• Maximizes model capabilities\n• Reduces need for fine-tuning\n• Cost-effective AI deployment",
      
      "# Basic Prompt Structure\n\n## Core Components\n• **Instruction** - What you want the AI to do\n• **Context** - Background information\n• **Input data** - Specific content to process\n• **Output format** - How to structure response\n\n## Example Structure\n```\n[Context]: You are an expert educator\n[Instruction]: Explain the concept of\n[Input]: Machine Learning\n[Format]: In simple terms for beginners\n```\n\n## Best Practices\n• Be specific and clear\n• Provide relevant context\n• Use examples when helpful\n• Specify desired output format",
      
      "# Prompt Types\n\n## Zero-shot Prompting\n• No examples provided\n• Relies on model's training\n• Simple and direct approach\n• Good for well-known tasks\n\n## Few-shot Prompting\n• Provide 1-5 examples\n• Show input-output patterns\n• Helps model understand format\n• More reliable results\n\n## Chain-of-Thought\n• Include reasoning steps\n• \"Let's think step by step\"\n• Improves complex problem solving\n• Better for mathematical tasks"
    ]
  },
  14: {
    id: 14,
    title: "Advanced Prompt Techniques",
    type: "reading",
    duration: "75 min",
    slides: [
      "# Advanced Prompt Techniques\n\n## Chain-of-Thought Prompting\n• Explicit reasoning steps\n• \"Think step by step\" instruction\n• Improves logical reasoning\n• Better performance on complex problems\n\n## Example\n```\nProblem: Calculate 15% tip on $67.50\nLet's think step by step:\n1. Convert 15% to decimal: 0.15\n2. Multiply: $67.50 × 0.15 = $10.13\n3. Therefore, the tip is $10.13\n```",
      
      "# Few-Shot Learning Patterns\n\n## Providing Examples\n• Show desired input-output format\n• Demonstrate reasoning patterns\n• Establish consistent style\n• Reduce ambiguity\n\n## Example Pattern\n```\nClassify the sentiment:\n\nText: \"I love this product!\"\nSentiment: Positive\n\nText: \"This is terrible quality.\"\nSentiment: Negative\n\nText: \"The delivery was fast.\"\nSentiment: ?\n```",
      
      "# Role-Based Prompting\n\n## Assigning Personas\n• \"You are an expert...\" \n• Provides context and expertise\n• Influences response style\n• Improves domain knowledge\n\n## Examples\n• \"You are a financial advisor...\"\n• \"Act as a creative writer...\"\n• \"You are a technical documentation expert...\"\n\n## Benefits\n• More specialized responses\n• Consistent expertise level\n• Appropriate terminology\n• Domain-specific insights",
      
      "# Template and Format Control\n\n## Output Structuring\n• JSON format specifications\n• Table layouts\n• Bullet point lists\n• Specific word counts\n\n## Template Example\n```\nGenerate a product review in this format:\n\nProduct: [Product Name]\nRating: [1-5 stars]\nPros: [List 3 advantages]\nCons: [List 2 disadvantages]\nRecommendation: [Yes/No with reason]\n```\n\n## Advanced Formatting\n• Markdown formatting\n• Code block specifications\n• Multi-language outputs\n• Custom delimiters"
    ]
  },
  15: {
    id: 15,
    title: "Industry Applications & Case Studies",
    type: "reading",
    duration: "50 min",
    slides: [
      "# Industry Applications & Case Studies\n\n## Content Creation\n• **Blog writing** - SEO-optimized articles\n• **Social media** - Engaging posts and captions\n• **Marketing copy** - Product descriptions, ads\n• **Technical documentation** - User manuals, APIs\n\n## Real Example\n```\nWrite a product description for:\nProduct: Wireless noise-canceling headphones\nTarget: Professionals working from home\nTone: Professional yet approachable\nLength: 150 words max\nInclude: Key features, benefits, use cases\n```",
      
      "# Customer Service Automation\n\n## AI Chatbots\n• **FAQ responses** - Instant answers\n• **Troubleshooting** - Step-by-step guides\n• **Escalation handling** - When to transfer to humans\n• **Multilingual support** - Global customer base\n\n## Prompt Strategy\n```\nYou are a customer service agent for [Company].\nGuidelines:\n- Be helpful and empathetic\n- Provide clear, actionable solutions\n- If unable to help, escalate to human agent\n- Keep responses under 100 words\n- Use company's brand voice\n```",
      
      "# Data Analysis & Research\n\n## Business Intelligence\n• **Report summarization** - Key insights extraction\n• **Data interpretation** - Pattern identification\n• **Trend analysis** - Future predictions\n• **Competitor research** - Market insights\n\n## Research Assistance\n• **Literature reviews** - Academic paper summaries\n• **Market research** - Industry analysis\n• **SWOT analysis** - Strategic planning\n• **Survey analysis** - Response categorization\n\n## Example Use Case\n```\nAnalyze this quarterly sales data:\n[Data provided]\n\nProvide:\n1. Top 3 performing products\n2. Seasonal trends identified\n3. Recommendations for Q4\n4. Potential concerns to address\n```",
      
      "# Education & Training\n\n## Personalized Learning\n• **Curriculum development** - Course outlines\n• **Assessment creation** - Quiz and test questions\n• **Explanation generation** - Complex concept simplification\n• **Study guides** - Exam preparation materials\n\n## Corporate Training\n• **Onboarding materials** - New employee guides\n• **Skill assessments** - Competency evaluation\n• **Compliance training** - Regulatory content\n• **Leadership development** - Management scenarios\n\n## Success Metrics\n• Improved learning outcomes\n• Reduced training time\n• Higher engagement rates\n• Cost-effective scaling"
    ]
  },
  16: {
    id: 16,
    title: "Hands-on Prompt Workshop",
    type: "reading",
    duration: "90 min",
    slides: [
      "# Hands-on Prompt Workshop\n\n## Workshop Objectives\n• Practice prompt engineering techniques\n• Learn from real-world scenarios\n• Develop prompt optimization skills\n• Build a personal prompt library\n\n## What You'll Create\n• Business email templates\n• Content generation prompts\n• Data analysis queries\n• Creative writing assistants",
      
      "# Exercise 1: Business Communication\n\n## Scenario\nCreate a prompt for generating professional emails\n\n## Your Task\nDevelop a prompt that generates:\n• Subject line\n• Greeting appropriate to relationship\n• Clear, concise body\n• Professional closing\n• Call-to-action when needed\n\n## Template to Build\n```\nGenerate a professional email:\nPurpose: [meeting request/follow-up/proposal]\nRecipient: [title and relationship]\nTone: [formal/semi-formal/friendly]\nKey points: [main message]\nDesired action: [what you want them to do]\n```",
      
      "# Exercise 2: Content Strategy\n\n## Scenario\nCreate social media content for a tech startup\n\n## Requirements\n• Platform-specific formatting\n• Brand voice consistency\n• Engagement optimization\n• Hashtag strategies\n\n## Prompt Development\n```\nCreate social media content for:\nPlatform: [LinkedIn/Twitter/Instagram]\nCompany: AI-powered fitness app\nBrand voice: Motivational yet scientific\nContent type: [educational/promotional/behind-scenes]\nGoal: [awareness/engagement/conversion]\nLength: [platform-appropriate]\n```",
      
      "# Exercise 3: Technical Documentation\n\n## Challenge\nSimplify complex technical concepts\n\n## Target Audience\n• Non-technical stakeholders\n• New team members\n• Customer support teams\n\n## Documentation Types\n• API explanations\n• Feature descriptions\n• Troubleshooting guides\n• User onboarding\n\n## Best Practices\n• Use analogies and metaphors\n• Progressive complexity\n• Visual descriptions\n• Practical examples",
      
      "# Prompt Optimization Techniques\n\n## Iterative Improvement\n1. **Start simple** - Basic prompt structure\n2. **Test and evaluate** - Review outputs\n3. **Identify issues** - What's missing or wrong?\n4. **Refine gradually** - Add constraints and examples\n5. **Document learnings** - Build your prompt library\n\n## Common Problems & Solutions\n• **Vague outputs** → Add specific examples\n• **Wrong tone** → Include tone descriptors\n• **Inconsistent format** → Provide templates\n• **Off-topic responses** → Add constraints\n• **Too lengthy** → Specify word limits\n\n## Building Your Prompt Library\n• Categorize by use case\n• Version control your prompts\n• Share successful patterns\n• Document performance metrics"
    ]
  },
  17: {
    id: 17,
    title: "Quiz: Prompt Engineering",
    type: "quiz",
    duration: "30 min",
    quiz: [
      {
        id: 1,
        question: "Which prompt engineering technique involves providing examples in your prompt?",
        options: [
          "Zero-shot prompting",
          "Few-shot prompting",
          "Chain-of-thought prompting",
          "Role prompting"
        ],
        correctAnswer: 1,
        explanation: "Few-shot prompting involves providing a few examples in your prompt to help the AI understand the desired output format or style."
      },
      {
        id: 2,
        question: "What is the main benefit of chain-of-thought prompting?",
        options: [
          "Faster processing speed",
          "Reduced token usage",
          "Improved reasoning and problem-solving",
          "Better memory efficiency"
        ],
        correctAnswer: 2,
        explanation: "Chain-of-thought prompting encourages AI models to break down complex problems into step-by-step reasoning, improving their problem-solving capabilities."
      },
      {
        id: 3,
        question: "When should you use role-based prompting?",
        options: [
          "When you want faster responses",
          "When you need domain-specific expertise in responses",
          "When you want shorter outputs",
          "When processing large datasets"
        ],
        correctAnswer: 1,
        explanation: "Role-based prompting (e.g., 'You are an expert...') provides context and expertise, resulting in more specialized and domain-appropriate responses."
      }
    ]
  },
  18: {
    id: 18,
    title: "AI Ethics and Bias",
    type: "reading",
    duration: "40 min",
    slides: [
      "# AI Ethics and Bias\n\n## Why Ethics Matter\n• AI impacts millions of lives\n• Decisions affect fairness and justice\n• Technology amplifies human biases\n• Responsibility for societal outcomes\n\n## Core Ethical Principles\n• **Fairness** - Equal treatment for all\n• **Transparency** - Explainable decisions\n• **Accountability** - Responsibility for outcomes\n• **Privacy** - Data protection and consent\n• **Beneficence** - Promoting human welfare",
      
      "# Understanding AI Bias\n\n## Types of Bias\n• **Historical bias** - Past discrimination in data\n• **Representation bias** - Unequal group representation\n• **Measurement bias** - Different quality data across groups\n• **Evaluation bias** - Inappropriate metrics\n• **Aggregation bias** - Assuming one model fits all\n\n## Real-World Examples\n• **Hiring algorithms** - Gender discrimination\n• **Facial recognition** - Racial accuracy differences\n• **Credit scoring** - Socioeconomic bias\n• **Criminal justice** - Recidivism prediction bias",
      
      "# Bias Mitigation Strategies\n\n## Data-Level Solutions\n• **Diverse datasets** - Representative samples\n• **Data auditing** - Regular bias checks\n• **Synthetic data** - Fill representation gaps\n• **Preprocessing** - Remove biased features\n\n## Algorithm-Level Solutions\n• **Fairness constraints** - Mathematical fairness metrics\n• **Adversarial debiasing** - Train against bias\n• **Multi-objective optimization** - Balance accuracy and fairness\n• **Regular monitoring** - Ongoing bias detection\n\n## Organizational Solutions\n• **Diverse teams** - Multiple perspectives\n• **Ethics committees** - Oversight and guidance\n• **Bias training** - Awareness and education\n• **External audits** - Independent evaluation"
    ]
  },
  19: {
    id: 19,
    title: "AI Safety and Alignment",
    type: "reading",
    duration: "35 min",
    slides: [
      "# AI Safety and Alignment\n\n## What is AI Safety?\n• Ensuring AI systems behave as intended\n• Preventing harmful or unintended consequences\n• Maintaining human control over AI systems\n• Building robust and reliable AI\n\n## Why It Matters\n• AI systems make consequential decisions\n• Unintended behaviors can cause harm\n• Systems may optimize for wrong objectives\n• Need to maintain human oversight",
      
      "# The Alignment Problem\n\n## Core Challenge\n• **Specification problem** - Defining correct objectives\n• **Goodhart's Law** - \"When a measure becomes a target, it ceases to be a good measure\"\n• **Reward hacking** - Gaming the system\n• **Distributional shift** - Performance degradation in new environments\n\n## Examples of Misalignment\n• **Paperclip maximizer** - Hypothetical AI that converts everything to paperclips\n• **Cleaning robot** - That breaks valuable items to complete task faster\n• **Game AI** - That finds unintended exploits to win\n• **Recommendation systems** - That promote addictive content",
      
      "# Safety Techniques\n\n## Technical Approaches\n• **Value learning** - Learn human preferences\n• **Inverse reinforcement learning** - Infer rewards from behavior\n• **Constitutional AI** - Built-in ethical principles\n• **Human feedback training** - RLHF (Reinforcement Learning from Human Feedback)\n\n## Robustness Methods\n• **Testing and validation** - Comprehensive evaluation\n• **Uncertainty quantification** - Know when unsure\n• **Interpretability** - Understand decision process\n• **Gradual deployment** - Careful rollout strategies\n\n## Governance and Oversight\n• **AI safety research** - Fundamental safety science\n• **Regulatory frameworks** - Policy and standards\n• **Industry cooperation** - Shared safety practices\n• **International coordination** - Global safety standards"
    ]
  },
  20: {
    id: 20,
    title: "Future of AI Technology",
    type: "reading",
    duration: "45 min",
    slides: [
      "# Future of AI Technology\n\n## Current Trajectory\n• Rapid advancement in language models\n• Improving multimodal capabilities\n• Better reasoning and problem-solving\n• Increased accessibility and democratization\n\n## Emerging Trends\n• **Foundation models** - General-purpose AI systems\n• **AI agents** - Autonomous task completion\n• **Multimodal AI** - Text, images, audio, video\n• **Neuromorphic computing** - Brain-inspired hardware",
      
      "# Breakthrough Technologies\n\n## Artificial General Intelligence (AGI)\n• Human-level intelligence across all domains\n• Self-improving capabilities\n• Transfer learning between tasks\n• Timeline: Uncertain, estimates vary widely\n\n## Quantum-AI Integration\n• **Quantum machine learning** - New algorithmic possibilities\n• **Quantum advantage** - Exponential speedups\n• **Hybrid systems** - Classical-quantum computing\n• **Cryptography implications** - Security considerations\n\n## Brain-Computer Interfaces\n• **Neural implants** - Direct brain-AI connection\n• **Thought-controlled interfaces** - Mental commands\n• **Memory augmentation** - Enhanced human cognition\n• **Therapeutic applications** - Medical treatments",
      
      "# Societal Implications\n\n## Economic Transformation\n• **Automation impact** - Job displacement and creation\n• **New industries** - AI-native business models\n• **Productivity gains** - Economic growth potential\n• **Wealth distribution** - Inequality considerations\n\n## Education Revolution\n• **Personalized learning** - Adaptive AI tutors\n• **Skill development** - Continuous learning needs\n• **Educational access** - Global knowledge democratization\n• **Human-AI collaboration** - New learning paradigms\n\n## Healthcare Advances\n• **Precision medicine** - Personalized treatments\n• **Drug discovery** - Accelerated development\n• **Diagnostic assistance** - Early disease detection\n• **Aging research** - Longevity implications",
      
      "# Preparing for the Future\n\n## Individual Strategies\n• **Lifelong learning** - Continuous skill development\n• **AI literacy** - Understanding AI capabilities and limitations\n• **Human skills focus** - Creativity, empathy, critical thinking\n• **Adaptability** - Flexibility in changing landscape\n\n## Organizational Readiness\n• **AI strategy development** - Long-term planning\n• **Workforce preparation** - Training and reskilling\n• **Ethical frameworks** - Responsible AI adoption\n• **Innovation culture** - Embracing technological change\n\n## Global Coordination\n• **International standards** - Common AI governance\n• **Research collaboration** - Shared knowledge advancement\n• **Risk mitigation** - Collective safety measures\n• **Equitable access** - Preventing AI divides"
    ]
  },
  21: {
    id: 21,
    title: "Final Comprehensive Assessment",
    type: "final_exam",
    duration: "45 min",
    quiz: [
      {
        id: 1,
        question: "Which AI milestone occurred at the 1956 Dartmouth Conference?",
        options: [
          "The first computer was invented",
          "The term 'Artificial Intelligence' was coined",
          "The internet was created",
          "The first neural network was built"
        ],
        correctAnswer: 1,
        explanation: "The 1956 Dartmouth Conference is widely considered the birth of AI as a field, where the term 'Artificial Intelligence' was first coined."
      },
      {
        id: 2,
        question: "What is the main difference between Narrow AI and General AI?",
        options: [
          "Narrow AI is faster than General AI",
          "Narrow AI is designed for specific tasks, General AI would have human-level intelligence across all domains",
          "General AI uses more memory than Narrow AI",
          "There is no significant difference"
        ],
        correctAnswer: 1,
        explanation: "Narrow AI (Weak AI) is designed for specific tasks, while General AI (Strong AI) would have human-level intelligence across all domains."
      },
      {
        id: 3,
        question: "In the AI hierarchy, how are AI, Machine Learning, and Deep Learning related?",
        options: [
          "They are completely separate fields",
          "Deep Learning is a subset of Machine Learning, which is a subset of AI",
          "AI is a subset of Machine Learning",
          "They all have equal scope"
        ],
        correctAnswer: 1,
        explanation: "Deep Learning is a subset of Machine Learning, which in turn is a subset of Artificial Intelligence, forming a nested hierarchy."
      },
      {
        id: 4,
        question: "Which type of machine learning uses labeled training data?",
        options: [
          "Unsupervised learning",
          "Supervised learning",
          "Reinforcement learning",
          "Semi-supervised learning"
        ],
        correctAnswer: 1,
        explanation: "Supervised learning uses labeled training data to learn patterns and make predictions on new, unseen data."
      },
      {
        id: 5,
        question: "What makes Deep Learning 'deep'?",
        options: [
          "It processes data very slowly",
          "It uses neural networks with multiple hidden layers",
          "It requires more storage space",
          "It only works with text data"
        ],
        correctAnswer: 1,
        explanation: "Deep Learning is called 'deep' because it uses neural networks with multiple hidden layers, allowing for complex pattern recognition."
      },
      {
        id: 6,
        question: "Which prompt engineering technique involves providing examples in your prompt?",
        options: [
          "Zero-shot prompting",
          "Few-shot prompting",
          "Chain-of-thought prompting",
          "Role prompting"
        ],
        correctAnswer: 1,
        explanation: "Few-shot prompting involves providing a few examples in your prompt to help the AI understand the desired output format or style."
      },
      {
        id: 7,
        question: "What is AI bias primarily caused by?",
        options: [
          "Slow processing speeds",
          "Biased training data or algorithms",
          "Too much computational power",
          "Using the wrong programming language"
        ],
        correctAnswer: 1,
        explanation: "AI bias is primarily caused by biased training data or algorithms that reflect societal biases, leading to unfair outcomes for certain groups."
      },
      {
        id: 8,
        question: "In reinforcement learning, what guides the agent's learning process?",
        options: [
          "Labeled examples from a dataset",
          "Rewards and penalties from the environment",
          "Pre-written rules and instructions",
          "Random exploration only"
        ],
        correctAnswer: 1,
        explanation: "Reinforcement learning agents learn through a system of rewards and penalties received from their environment based on their actions."
      },
      {
        id: 9,
        question: "Which of the following is NOT a common application of Convolutional Neural Networks (CNNs)?",
        options: [
          "Image recognition",
          "Computer vision tasks",
          "Natural language translation",
          "Medical image analysis"
        ],
        correctAnswer: 2,
        explanation: "CNNs are primarily used for visual tasks like image recognition, computer vision, and medical imaging. Natural language translation typically uses Transformers or RNNs."
      },
      {
        id: 10,
        question: "What is the primary goal of AI safety and alignment?",
        options: [
          "Making AI systems run faster",
          "Ensuring AI systems behave as intended and don't cause harm",
          "Reducing the cost of AI development",
          "Making AI systems use less memory"
        ],
        correctAnswer: 1,
        explanation: "AI safety and alignment focus on ensuring that AI systems behave as intended by their creators and don't cause unintended harm to humans or society."
      },
      {
        id: 11,
        question: "Which era marked the beginning of the deep learning revolution?",
        options: [
          "1950s - Birth of AI",
          "1980s - Expert systems",
          "2000s - Modern AI era",
          "1970s - First AI winter"
        ],
        correctAnswer: 2,
        explanation: "The 2000s marked the beginning of the modern AI era, with the deep learning revolution starting around 2006 and gaining momentum through the 2010s."
      },
      {
        id: 12,
        question: "What is chain-of-thought prompting designed to improve?",
        options: [
          "Processing speed",
          "Memory usage",
          "Complex reasoning and problem-solving",
          "Data storage efficiency"
        ],
        correctAnswer: 2,
        explanation: "Chain-of-thought prompting encourages AI models to break down complex problems into step-by-step reasoning, improving their problem-solving capabilities."
      }
    ]
  }
};

export const LessonViewer: React.FC<LessonViewerProps> = ({ lesson, onComplete, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);

  const lessonData = sampleLessonContent[lesson.id] || lesson;
  const isQuizLesson = lesson.type === 'quiz' || !lessonData.slides;
  const totalSlides = lessonData.slides?.length || 0;
  const totalQuestions = lessonData.quiz?.length || 0;

  const handleNextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (lessonData.quiz && !showQuiz) {
      setShowQuiz(true);
    } else {
      handleLessonComplete();
    }
  };

  const handlePrevSlide = () => {
    if (showQuiz && currentQuestion === 0) {
      setShowQuiz(false);
      setCurrentSlide(totalSlides - 1);
    } else if (showQuiz) {
      setCurrentQuestion(Math.max(0, currentQuestion - 1));
    } else {
      setCurrentSlide(Math.max(0, currentSlide - 1));
    }
  };

  const handleAnswerSelect = (questionId: number, answerIndex: number) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      submitQuiz();
    }
  };

  const submitQuiz = () => {
    console.log('submitQuiz - selectedAnswers:', selectedAnswers);
    console.log('submitQuiz - lessonData.quiz:', lessonData.quiz);
    
    const correctAnswers = lessonData.quiz?.reduce((count, question) => {
      const userAnswer = selectedAnswers[question.id];
      const correctAnswer = question.correctAnswer;
      const isCorrect = userAnswer === correctAnswer;
      
      console.log(`Question ${question.id}: user=${userAnswer}, correct=${correctAnswer}, isCorrect=${isCorrect}`);
      
      return isCorrect ? count + 1 : count;
    }, 0) || 0;

    console.log('correctAnswers:', correctAnswers, 'totalQuestions:', totalQuestions);
    
    const percentage = Math.round((correctAnswers / totalQuestions) * 100);
    setScore(percentage);
    setQuizCompleted(true);

    if (percentage >= 70) {
      toast.success(`Quiz completed! Score: ${percentage}%`);
    } else {
      toast.error(`Quiz score: ${percentage}%. You need 80% to pass. Please try again.`);
    }
  };

  const handleLessonComplete = () => {
    if (lessonData.quiz && !quizCompleted) {
      toast.error('Please complete the quiz to finish this lesson.');
      return;
    }
    
    if (lessonData.quiz && score < 80) {
      toast.error('You need to score at least 80% on the quiz to complete this lesson.');
      return;
    }

    // Pass the quiz score if this is a quiz lesson
    const finalScore = lessonData.quiz ? score : undefined;
    onComplete(finalScore);
    toast.success('Lesson completed successfully!');
  };

  const retryQuiz = () => {
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setQuizCompleted(false);
    setScore(0);
  };

  const getIcon = () => {
    switch (lesson.type) {
      case 'quiz': return <FileText className="h-5 w-5" />;
      case 'reading': return <Book className="h-5 w-5" />;
      default: return <Book className="h-5 w-5" />;
    }
  };

  const getCurrentProgress = () => {
    if (isQuizLesson) {
      return quizCompleted ? 100 : (currentQuestion / totalQuestions) * 100;
    }
    if (showQuiz) {
      const slideProgress = totalSlides;
      const quizProgress = currentQuestion;
      return ((slideProgress + quizProgress) / (totalSlides + totalQuestions)) * 100;
    }
    return ((currentSlide + 1) / totalSlides) * 100;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" onClick={onClose}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Course
          </Button>
          <Badge variant="secondary" className="flex items-center gap-2">
            {getIcon()}
            {lesson.type}
          </Badge>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold">{lesson.title}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              {lesson.duration}
            </div>
          </div>
          <Progress value={getCurrentProgress()} className="h-2" />
          <p className="text-sm text-muted-foreground mt-2">
            {showQuiz || isQuizLesson ? 
              `Question ${currentQuestion + 1} of ${totalQuestions}` :
              `Slide ${currentSlide + 1} of ${totalSlides}`
            }
          </p>
        </div>

        {/* Content */}
        <Card className="min-h-[500px]">
          <CardContent className="p-8">
            {!showQuiz && !isQuizLesson && lessonData.slides && (
              <div className="prose max-w-none dark:prose-invert">
                {lessonData.slides[currentSlide]?.split('\n').map((line, index) => {
                  if (line.startsWith('# ')) {
                    return <h1 key={index} className="text-3xl font-bold mb-6">{line.substring(2)}</h1>;
                  }
                  if (line.startsWith('## ')) {
                    return <h2 key={index} className="text-2xl font-semibold mb-4 mt-6">{line.substring(3)}</h2>;
                  }
                  if (line.startsWith('• ')) {
                    return <li key={index} className="mb-2">{line.substring(2)}</li>;
                  }
                  if (line.startsWith('**') && line.endsWith('**')) {
                    return <p key={index} className="font-bold mb-3">{line.substring(2, line.length - 2)}</p>;
                  }
                  if (line.trim() === '') {
                    return <br key={index} />;
                  }
                  return <p key={index} className="mb-3">{line}</p>;
                })}
              </div>
            )}

            {(showQuiz || isQuizLesson) && lessonData.quiz && !quizCompleted && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold mb-2">Quiz Time!</h2>
                  <p className="text-muted-foreground">Answer all questions to complete this lesson</p>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      Question {currentQuestion + 1} of {totalQuestions}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg mb-6">{lessonData.quiz[currentQuestion]?.question}</p>
                    
                    <RadioGroup
                      value={selectedAnswers[lessonData.quiz[currentQuestion]?.id]?.toString()}
                      onValueChange={(value) => 
                        handleAnswerSelect(lessonData.quiz[currentQuestion]?.id, parseInt(value))
                      }
                      className="space-y-3"
                    >
                      {lessonData.quiz[currentQuestion]?.options.map((option, index) => {
                        const isSelected = selectedAnswers[lessonData.quiz[currentQuestion]?.id] === index;
                        return (
                          <div 
                            key={index} 
                            className={`flex items-center space-x-2 p-3 rounded-lg border transition-colors ${
                              isSelected 
                                ? 'border-primary bg-primary/10 ring-2 ring-primary/20' 
                                : 'border-border hover:bg-muted/50'
                            }`}
                          >
                            <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                            <Label 
                              htmlFor={`option-${index}`} 
                              className={`flex-1 cursor-pointer ${
                                isSelected ? 'text-primary font-medium' : ''
                              }`}
                            >
                              {option}
                            </Label>
                          </div>
                        );
                      })}
                    </RadioGroup>
                  </CardContent>
                </Card>
              </div>
            )}

            {quizCompleted && (
              <div className="text-center space-y-6">
                <div className={`p-6 rounded-lg ${score >= 80 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    {score >= 80 ? (
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    ) : (
                      <FileText className="h-8 w-8 text-red-600" />
                    )}
                    <h2 className={`text-2xl font-bold ${score >= 80 ? 'text-green-800' : 'text-red-800'}`}>
                      Quiz {score >= 80 ? 'Passed!' : 'Failed'}
                    </h2>
                  </div>
                  <p className={`text-lg mb-4 ${score >= 80 ? 'text-green-700' : 'text-red-700'}`}>
                    Your Score: {score}%
                  </p>
                  <p className={`${score >= 80 ? 'text-green-600' : 'text-red-600'}`}>
                    {score >= 80 
                      ? 'Congratulations! You can proceed to the next lesson.'
                      : 'You need at least 80% to pass. Please review the material and try again.'
                    }
                  </p>
                </div>

                <div className="space-y-4">
                  {lessonData.quiz?.map((question, index) => (
                    <Card key={question.id} className="text-left">
                      <CardContent className="p-4">
                        <p className="font-medium mb-2">Q{index + 1}: {question.question}</p>
                        <p className={`text-sm mb-2 ${
                          selectedAnswers[question.id] === question.correctAnswer 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          Your answer: {question.options[selectedAnswers[question.id]]}
                        </p>
                        {selectedAnswers[question.id] !== question.correctAnswer && (
                          <p className="text-sm text-green-600 mb-2">
                            Correct answer: {question.options[question.correctAnswer]}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          {question.explanation}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-6">
          <Button 
            variant="outline" 
            onClick={handlePrevSlide}
            disabled={!showQuiz && currentSlide === 0 && currentQuestion === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          <div className="flex gap-2">
            {quizCompleted && score < 80 && (
              <Button variant="outline" onClick={retryQuiz}>
                Retry Quiz
              </Button>
            )}
            
            {!quizCompleted && (showQuiz || isQuizLesson) && (
              <Button 
                onClick={handleNextQuestion}
                disabled={selectedAnswers[lessonData.quiz?.[currentQuestion]?.id] === undefined}
              >
                {currentQuestion === totalQuestions - 1 ? 'Submit Quiz' : 'Next Question'}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}

            {!showQuiz && !isQuizLesson && (
              <Button onClick={currentSlide === totalSlides - 1 && !lessonData.quiz ? handleLessonComplete : handleNextSlide}>
                {currentSlide === totalSlides - 1 ? 
                  (lessonData.quiz ? 'Start Quiz' : 'Complete Lesson') : 
                  'Next Slide'
                }
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}

            {(quizCompleted && score >= 80) && (
              <Button onClick={handleLessonComplete} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete Lesson
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};